/**
 * Field photo lightbox (TECH-JOB-SCREEN §3e) — the "make-or-break" viewer
 * for Site History galleries on iPad.
 *
 * Built on yet-another-react-lightbox rather than hand-rolled pointer
 * math: it ships proven touch gestures — horizontal swipe between the
 * group's photos, pinch-zoom, double-tap zoom, pan with momentum, and
 * pull-down-to-close — that a scroll-wheel/buttons implementation can't
 * match on a job site. Renders in a portal above everything with a dark
 * backdrop; only current ± 1 images are preloaded (weak signal, old
 * iPads). A small caption (date · device label) overlays the bottom.
 */
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

export interface LightboxPhoto {
    /** Full-size (inline) image URL, token included. */
    src: string;
    /** Caption line, e.g. "Jul 6, 2026 · 3/4\" RP — Wilkins 375XL". */
    caption?: string | null;
}

interface PhotoLightboxProps {
    open: boolean;
    onClose: () => void;
    photos: LightboxPhoto[];
    index: number;
    /**
     * Optional action bar (Retake / Delete) rendered above the caption, inside
     * the slide footer so it stays thumb-reachable on an iPad rather than in
     * the top toolbar. Optional so read-only galleries pass nothing.
     */
    actions?: React.ReactNode;
    /** Fires as the tech swipes, so `actions` can target the visible photo. */
    onIndexChange?: (index: number) => void;
}

export function PhotoLightbox({ open, onClose, photos, index, actions, onIndexChange }: PhotoLightboxProps) {
    // Base slide types carry no caption field; map back from src instead of
    // pulling in the Captions plugin (whose UI is heavier than one pill).
    const captionBySrc = new Map(photos.map((p) => [p.src, p.caption ?? null]));
    return (
        <Lightbox
            open={open}
            close={onClose}
            index={index}
            slides={photos.map((p) => ({ src: p.src }))}
            plugins={[Zoom]}
            carousel={{ preload: 1, finite: photos.length <= 1 }}
            on={{ view: ({ index: i }) => onIndexChange?.(i) }}
            controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
            zoom={{
                maxZoomPixelRatio: 4,
                // Pinch + double-tap are the primary zoom mechanisms on iPad;
                // scroll-to-zoom stays off so two-finger scrolls don't hijack.
                scrollToZoom: false,
                doubleTapDelay: 300,
                doubleClickDelay: 300,
            }}
            styles={{
                container: { backgroundColor: "rgba(0, 0, 0, 0.92)" },
            }}
            render={{
                slideFooter: ({ slide }) => {
                    const caption = "src" in slide ? captionBySrc.get(slide.src) : null;
                    if (!caption && !actions) return null;
                    return (
                        <div
                            className="pointer-events-none absolute inset-x-0 bottom-3 flex flex-col items-center gap-2"
                            style={{ zIndex: 1 }}
                        >
                            {caption && (
                                <span className="inline-block rounded-full bg-black/60 px-3 py-1 text-xs text-white/90">
                                    {caption}
                                </span>
                            )}
                            {actions && <div className="pointer-events-auto flex items-center gap-2">{actions}</div>}
                        </div>
                    );
                },
            }}
        />
    );
}
